import React from 'react';
import {Tabs} from 'antd';
import {LevelChart} from './LevelChart';
import {FngGauge} from './FngGauge';
import ResearchKlineTab from '../ResearchKlineTab';
import {HedgeSummaryBar} from './HedgeSummaryBar';
import {DcaSummaryBar} from './DcaSummaryBar';
import type {ResearchReportContent, ResearchReportDetail} from '../../../../types/researchReport';
import '../ResearchReportPage.css';

/** 偏多/偏空 bias 中文映射 */
const BIAS_TEXT: Record<string, string> = {bullish: '偏多', bearish: '偏空', neutral: '盘整'};

/** 多空信号类（对齐 K 线形态语义：多=绿 bull，空=红 bear） */
const signalClass = (signal: string): string =>
    '多' === signal ? ' rr-bull' : ('空' === signal ? ' rr-bear' : '');

/** 风险等级 CSS 类（高/中/低 → rr-risk-high/mid/low） */
const riskLevelClass = (level: string): string =>
    '高' === level ? 'rr-risk-high' : ('中' === level ? 'rr-risk-mid' : 'rr-risk-low');

/** 涨跌文本方向类：+ → 绿 bull，- → 红 bear（对齐 K 线形态页语义） */
const trendClass = (value?: string): string =>
    null != value && value.startsWith('+') ? ' rr-bull' : (null != value && value.startsWith('-') ? ' rr-bear' : '');

/** 涨跌卡整卡淡染类（独立类名，避免 .rr-bull 全局着色污染整卡文字） */
const trendCardClass = (value?: string): string =>
    null != value && value.startsWith('+') ? ' rr-trend-bull' : (null != value && value.startsWith('-') ? ' rr-trend-bear' : '');

/** 速览衍生品行语义着色：费率正负 / OI 24h 变化 / 买入占比 50 线 / 多空比 1 线 */
const semanticLineClass = (label: string, line: string): string => {
    if ('资金费率' === label) {
        if (line.includes('+')) return ' rr-bull';
        if (line.includes('-')) return ' rr-bear';
    }
    if ('持仓量 OI' === label) {
        const m = line.match(/24h\s*([+-])/);
        if (null != m) return '+' === m[1] ? ' rr-bull' : ' rr-bear';
    }
    if ('主动买入占比' === label) {
        const m = line.match(/([\d.]+)%/);
        if (null != m) return parseFloat(m[1]) >= 50 ? ' rr-bull' : ' rr-bear';
    }
    if ('多空账户比' === label) {
        const m = line.match(/([\d.]+)/);
        if (null != m) return parseFloat(m[1]) >= 1 ? ' rr-bull' : ' rr-bear';
    }
    return '';
};

/** 涨跌文本箭头前缀：+ → ▲，- → ▼ */
const trendArrow = (value?: string): string =>
    null != value && value.startsWith('+') ? '▲ ' : (null != value && value.startsWith('-') ? '▼ ' : '');

/** 恐惧贪婪 value 解析（"69 / 100 Greed" → 数值+分类；"--" → null） */
const parseFearGreed = (value?: string): { num: number; text: string } | null => {
    if (null == value || '--' === value) {
        return null;
    }
    const m = value.match(/^([\d.]+)\s*\/\s*100(?:\s+(.+))?$/);
    return null == m ? null : {num: Number(m[1]), text: m[2] ?? ''};
};

/** 要闻情感文案：POSITIVE→积极 / NEGATIVE→消极 / NEUTRAL→中性 */
const sentimentText = (s: string): string =>
    'POSITIVE' === s ? '积极' : ('NEGATIVE' === s ? '消极' : '中性');

/** 要闻情感着色：积极绿 / 消极红 / 中性黄（对齐多空语义） */
const sentimentClass = (s: string): string =>
    'POSITIVE' === s ? ' rr-bull' : ('NEGATIVE' === s ? ' rr-bear' : ' rr-amber');

/** 研报正文（页面与 /hedge 执行记录弹窗共用；不包含页面工具栏与右侧历史面板） */
export const ResearchReportBody: React.FC<{ detail: ResearchReportDetail }> = ({detail}) => {
    const content = detail?.content;
    if (!content) {
        return null;
    }
    return (
        <div className="rr-report">
            {renderHeader(content)}
            {renderVerdict(content)}
            {renderKeyMetrics(content, detail?.id)}
            {renderLevels(content)}
            {renderTechnicals(content)}
            {renderFunding(content)}
            {renderMacro(content)}
            {renderNews(content)}
            {renderFundamentals(content)}
            {renderPlans(content)}
            {renderRisks(content)}
            <div className="rr-disc">{content.disclaimer}</div>
        </div>
    );
};

/**
 * 头部：标题 + 数据截至时间
 */
function renderHeader(content: ResearchReportContent) {
    return (
        <div className="rr-header">
            <h1>{content.meta.symbol} 行情研报（现货/永续双口径）</h1>
            <div className="rr-subtitle">数据截至 {content.meta.dataAsOfText} | 程序规则自动生成</div>
        </div>
    );
}

/**
 * 核心结论：蓝紫渐变 tldr 区 + bias 徽章
 */
function renderVerdict(content: ResearchReportContent) {
    const {verdict} = content;
    return (
        <div className="rr-tldr">
            <h2>核心结论</h2>
            <p>{verdict.summaryText}</p>
            <div className="rr-verdict-chip">{verdict.verdictText}</div>
            <div className="rr-verdict-badge">
                规则投票得分 {verdict.score} · {BIAS_TEXT[verdict.bias] ?? verdict.bias}
            </div>
        </div>
    );
}

/**
 * 一、关键数据速览：5 Tab（数据速览卡片 / 现货 K线 / 永续 K线 / 对冲交易 / DCA交易）
 * K线 tab 读研报生成时刻的 K 线快照（5m/1H/1D × 240 根，指标冻结只读 + 研报价位参考线）
 */
function renderKeyMetrics(content: ResearchReportContent, reportId?: number) {
    const {keyMetrics} = content;
    return (
        <div className="rr-section">
            <h2>{keyMetrics.title}</h2>
            <Tabs
                className="rr-metrics-tabs"
                items={[
                    {key: 'overview', label: '数据速览', children: renderMetricCards(content)},
                    {
                        key: 'spot', label: '现货 K线',
                        children: <ResearchKlineTab reportId={reportId} swap={false}
                                                    levels={content.levels?.levels ?? []}/>
                    },
                    {
                        key: 'swap', label: '永续 K线',
                        children: <ResearchKlineTab reportId={reportId} swap={true}
                                                    levels={content.levels?.levels ?? []}/>
                    },
                    {
                        key: 'hedge', label: '对冲交易',
                        children: <HedgeSummaryBar reportId={reportId}/>
                    },
                    {
                        key: 'dca', label: 'DCA交易',
                        children: <DcaSummaryBar reportId={reportId}/>
                    },
                ]}
            />
        </div>
    );
}

/**
 * 数据速览 Tab：5×3 指标卡（价格大卡 / 涨跌卡 / 普通卡 / 恐惧贪婪色带卡）
 */
function renderMetricCards(content: ResearchReportContent) {
    const cards = content.keyMetrics.cards;
    const priceCards = cards.filter(c => 'price' === c.type);
    const changeCards = cards.filter(c => 'change' === c.type);
    const fngCard = cards.find(c => 'feargreed' === c.type);
    const restCards = cards.filter(c => 'price' !== c.type && 'change' !== c.type && 'feargreed' !== c.type);
    const fng = fngCard ? parseFearGreed(fngCard.value) : null;
    return (
        <div className="rr-metrics">
            {priceCards.map((card, i) => (
                <div key={`p${i}`} className="rr-metric-card rr-price-card">
                    <div className="rr-metric-label">{card.label}</div>
                    <div className="rr-price-value">{card.value}</div>
                    <span className={`rr-change-badge${trendClass(card.change)}`}>{card.change}</span>
                    {card.source ? <div className="rr-metric-sub">{card.source}</div> : null}
                </div>
            ))}
            {changeCards.map((card, i) => (
                <div key={`c${i}`} className={`rr-metric-card${trendCardClass(card.value)}`}>
                    <div className="rr-metric-label">{card.label}</div>
                    <div className={`rr-metric-value rr-metric-trend${trendClass(card.value)}`}>
                        {trendArrow(card.value)}{card.value}
                    </div>
                </div>
            ))}
            {restCards.map((card, i) => {
                const lines = card.value.split('\n');
                return (
                    <div key={`m${i}`} className="rr-metric-card">
                        <div className="rr-metric-label">{card.label}</div>
                        <div className="rr-metric-value rr-metric-lines">
                            {lines.map((line, j) => {
                                // 拆出所名前缀（OKX/Bybit）与尾部注释（如"（账户口径）"）单独弱化，数值部分保留行样式
                                const srcTag = line.match(/^(OKX|Bybit)\s+(.+)$/);
                                const rest = srcTag ? srcTag[2] : line;
                                const note = rest.match(/（账户口径）$/);
                                const main = note ? rest.slice(0, rest.length - note[0].length) : rest;
                                return (
                                    <div key={j}
                                         className={'rr-metric-line' + (0 === j ? ' rr-metric-line-primary' : '')
                                             + (lines.length > 1
                                                 ? (0 === j ? ' rr-metric-line-src rr-src-okx' : ' rr-metric-line-src rr-src-bybit')
                                                 : '')
                                             + semanticLineClass(card.label, line)}>
                                        {srcTag ? <span className="rr-line-tag">{srcTag[1]}</span> : null}
                                        {main}
                                        {note ? <span className="rr-line-note">{note[0]}</span> : null}
                                    </div>
                                );
                            })}
                        </div>
                        {card.source ? <div className="rr-metric-sub">{card.source}</div> : null}
                    </div>
                );
            })}
            {fngCard ? (
                <div className="rr-metric-card rr-fng-card">
                    <div className="rr-metric-label">{fngCard.label}
                        {fngCard.source ? <span className="rr-price-source">{fngCard.source}</span> : null}
                    </div>
                    {null != fng ? (
                        <>
                            <FngGauge value={fng.num}/>
                            <div className="rr-fng-text">{fng.text}</div>
                        </>
                    ) : (
                        <div className="rr-metric-value rr-fng-empty">--</div>
                    )}
                </div>
            ) : null}
        </div>
    );
}

/**
 * 二、关键支撑阻力位全景：ECharts 条形图 + 价位表
 */
function renderLevels(content: ResearchReportContent) {
    const {levels} = content;
    return (
        <div className="rr-section">
            <h2>{levels.title}</h2>
            <LevelChart levels={levels.levels}/>
            <table className="rr-table">
                <thead>
                <tr>
                    <th>价位（现货）</th>
                    <th>永续价位</th>
                    <th>角色</th>
                    <th>说明</th>
                </tr>
                </thead>
                <tbody>
                {levels.levels.map((node, i) => (
                    <tr key={i} className={'current' === node.type ? 'rr-current-row' : ''}>
                        <td><strong>{'current' === node.type ? `~$${node.price}` : `$${node.price}`}</strong></td>
                        <td>{node.swapPrice ? `$${node.swapPrice}` : '--'}</td>
                        <td className={`rr-role${'resistance' === node.type ? ' rr-bear' : 'support' === node.type ? ' rr-bull' : ' rr-amber'}`}>
                            {node.role}
                        </td>
                        <td className="rr-desc">{node.description}
                            {node.distancePct ? `（距现价 ${node.distancePct}）` : ''}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * 三、技术面综合研判：现货/永续双值列表 + 总结句
 */
function renderTechnicals(content: ResearchReportContent) {
    const {technicals} = content;
    return (
        <div className="rr-section">
            <h2>{technicals.title}</h2>
            <table className="rr-table">
                <thead>
                <tr>
                    <th>指标</th>
                    <th>现货</th>
                    <th>永续</th>
                    <th>信号</th>
                    <th>解读</th>
                </tr>
                </thead>
                <tbody>
                {technicals.rows.map((row, i) => (
                    <tr key={i}>
                        <td>{row.indicator}</td>
                        <td>{row.spotValue}</td>
                        <td>{row.swapValue}</td>
                        <td className={signalClass(row.signal)}>{row.signal}</td>
                        <td className="rr-desc">{row.interpretation}</td>
                    </tr>
                ))}
                </tbody>
            </table>
            <div className="rr-summary-box">
                <strong>技术面综合：</strong>{technicals.summary}
            </div>
        </div>
    );
}

/**
 * 四、资金面：现货/永续两个小节表
 */
function renderFunding(content: ResearchReportContent) {
    const {funding} = content;
    return (
        <div className="rr-section">
            <h2>{funding.title}</h2>
            <h3>{funding.spot.title}</h3>
            {renderFundingTable(funding.spot.rows)}
            <h3>{funding.swap.title}</h3>
            {renderFundingTable(funding.swap.rows)}
        </div>
    );
}

function renderFundingTable(rows: Array<{ name: string; value: string; signal: string; interpretation: string }>) {
    return (
        <table className="rr-table">
            <thead>
            <tr>
                <th>指标</th>
                <th>数据</th>
                <th>信号</th>
                <th>解读</th>
            </tr>
            </thead>
            <tbody>
            {rows.map((row, i) => (
                <tr key={i}>
                    <td>{row.name}</td>
                    <td>{row.value}</td>
                    <td className={signalClass(row.signal)}>{row.signal}</td>
                    <td className="rr-desc">{row.interpretation}</td>
                </tr>
            ))}
            </tbody>
        </table>
    );
}

/**
 * 五、宏观催化与情景分析：事件日历 + CPI 三情景
 */
function renderMacro(content: ResearchReportContent) {
    const {macro} = content;
    return (
        <div className="rr-section">
            <h2>{macro.title}</h2>
            <h3>近期宏观日历</h3>
            {macro.events.length ? (
                <table className="rr-table">
                    <thead>
                    <tr>
                        <th>事件</th>
                        <th>时间</th>
                        <th>倒计时</th>
                        <th>提示</th>
                    </tr>
                    </thead>
                    <tbody>
                    {macro.events.map((event, i) => (
                        <tr key={i}>
                            <td>{event.name}</td>
                            <td>{event.timeText}</td>
                            <td>{event.daysAway >= 0 ? `约 ${event.daysAway} 天` : '--'}</td>
                            <td className="rr-desc">{event.hint}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            ) : (
                <p className="rr-note">未来暂无已内置的宏观事件</p>
            )}
            <h3>CPI 情景推演</h3>
            <p className="rr-note">{macro.cpi.note}</p>
            <table className="rr-table">
                <thead>
                <tr>
                    <th>情景</th>
                    <th>信号</th>
                    <th>推演</th>
                </tr>
                </thead>
                <tbody>
                {macro.cpi.scenarios.map((scenario, i) => (
                    <tr key={i}>
                        <td>{scenario.name}</td>
                        <td className={signalClass(scenario.signal)}>{scenario.signal}</td>
                        <td className="rr-desc">{scenario.desc}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * 六、财经要闻：近 24h 已分析要闻（品种相关优先），情感着色 + LLM 影响分析
 */
function renderNews(content: ResearchReportContent) {
    const {news} = content;
    if (!news) {
        return null;
    }
    return (
        <div className="rr-section">
            <h2>{news.title}</h2>
            {news.empty || !news.items?.length ? (
                <p className="rr-note">{news.note ?? '近 24h 暂无已分析的财经要闻'}</p>
            ) : (
                <div className="rr-news-list">
                    {news.items.map((item, i) => (
                        <div key={i} className="rr-news-item">
                            <div className="rr-news-head">
                                <span className={`rr-news-sentiment${sentimentClass(item.sentiment)}`}>
                                    {sentimentText(item.sentiment)}
                                </span>
                                {item.related ? <span className="rr-news-related">相关</span> : null}
                                <span className="rr-news-title">{item.title}</span>
                            </div>
                            <div className="rr-news-meta">
                                {item.source} · {item.time}
                                {item.url ? (
                                    <a href={item.url} target="_blank" rel="noreferrer"
                                       className="rr-news-link">原文</a>
                                ) : null}
                            </div>
                            {item.reason ? <div className="rr-news-reason">{item.reason}</div> : null}
                            {item.summary ? <div className="rr-news-summary">{item.summary}</div> : null}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * 七、基本面与生态进展：字典行直填
 */
function renderFundamentals(content: ResearchReportContent) {
    const {fundamentals} = content;
    return (
        <div className="rr-section">
            <h2>{fundamentals.title}</h2>
            <table className="rr-table">
                <thead>
                <tr>
                    <th>维度</th>
                    <th>内容</th>
                </tr>
                </thead>
                <tbody>
                {fundamentals.rows.map((row, i) => (
                    <tr key={i}>
                        <td>{row.dimension}</td>
                        <td className="rr-desc">{row.content}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * 八、操作建议与交易计划：判断卡 + 情景卡 + 止盈/止损/失效卡
 */
function renderPlans(content: ResearchReportContent) {
    const {plans} = content;
    const scenarioCardClass = 'bullish' === content.verdict.bias ? ' buy'
        : ('bearish' === content.verdict.bias ? ' sell' : ' watch');
    return (
        <div className="rr-section">
            <h2>{plans.title}</h2>

            <div className="rr-plan-card watch">
                <h3>当前判断：{plans.judgement}</h3>
                <p>{plans.position}</p>
            </div>

            <div className={`rr-plan-card${scenarioCardClass}`}>
                <h3>情景计划</h3>
                {plans.scenarios.map((scenario) => (
                    <p key={scenario.tag}>
                        <span className={`rr-level-tag rr-tag-${scenario.tag.toLowerCase()}`}>
                            情景{scenario.tag}：{scenario.name}
                        </span>
                        {scenario.text}
                        {scenario.note ? <span className="rr-scenario-note">（{scenario.note}）</span> : null}
                    </p>
                ))}
            </div>

            <div className="rr-plan-card sell">
                <h3>止盈目标</h3>
                <p className="rr-note">{plans.takeProfit.note}</p>
                {plans.takeProfit.items.map((item, i) => (
                    <p key={i}>
                        <span className="rr-level-tag rr-tag-green">{item.level}</span>
                        {item.price}
                    </p>
                ))}
            </div>

            <div className="rr-plan-card sell">
                <h3>止损纪律</h3>
                <p>{plans.stopLoss.hard.label}：<strong>{plans.stopLoss.hard.value}</strong></p>
                <p>{plans.stopLoss.trend.label}：<strong>{plans.stopLoss.trend.value}</strong></p>
                <p>{plans.stopLoss.range.label}：{plans.stopLoss.range.value}</p>
            </div>

            <div className="rr-plan-card watch">
                <h3>失效条件</h3>
                <p>{plans.invalidation}</p>
            </div>
        </div>
    );
}

/**
 * 九、风险提示：规则库触发列表
 */
function renderRisks(content: ResearchReportContent) {
    const {risks} = content;
    return (
        <div className="rr-section">
            <h2>{risks.title}</h2>
            <p className="rr-note">{risks.summary}</p>
            {risks.risks.length ? (
                <ul className="rr-risk-list">
                    {risks.risks.map((risk, i) => (
                        <li key={i}>
                            <span className={`rr-risk-level ${riskLevelClass(risk.level)}`}>{risk.level}</span>
                            <strong>{risk.name}：</strong>{risk.desc}
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="rr-note">当前未触发规则库风险项，仍需注意加密资产固有波动风险</p>
            )}
        </div>
    );
}

export default ResearchReportBody;
